import { useEffect, useState } from 'react';
import { AiOutlineCheck } from 'react-icons/ai';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  createSubscription,
  getSubscriptionList,
  updateSubscription,
  updateSubscriptionStatus
} from '../../api/subscription';
import { logoutUser } from '../../api/Auth';
import DiscountArrorw from '../../assets/Discount-arrorw.png';
import ToastNotification from '../../common/ToastNotification';
import { BackButton } from '../../common/BackButton';
import { useSocket } from '../../Contexts/SocketContext';
import { createRetailerSubscriptionPlan } from '../../api/RetailerSubscription';

const Subscription = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const businessType = sessionStorage.getItem('businessType');
  const shopId = sessionStorage.getItem('shopId');
  const razorPayKey = process.env.REACT_APP_RAZORPAY_KEY;
  const { disconnectSocket } = useSocket();

  const [billingType, setBillingType] = useState<string>('monthly');
  const [subscriptionPlanList, setSubscriptionPlanList] = useState<any[]>();
  const [subscriptionPlanDetail, setSubscriptionPlanDetail] = useState<any>();
  const [filteredPlans, setFilteredPlans] = useState<any>();
  const [loading, setLoading] = useState(true);
  const [disable, setDisable] = useState(false);

  const getQueryParam = (key: any) => {
    const params = new URLSearchParams(location.search);
    return params.get(key);
  };

  const recentPlanType = getQueryParam('recentPlanType');
  const updatePlan = getQueryParam('updatePlan');

  const getSubscription = async () => {
    try {
      const data = await getSubscriptionList();
      const scriptionPlan = data?.payload as any;
      setSubscriptionPlanDetail(scriptionPlan);
      setSubscriptionPlanList(scriptionPlan.plans);
    } catch (err: any) {
      console.error('Error fetching data', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSubscription();
  }, []);

  const handleLogoutClick = async () => {
    try {
      const uuid = sessionStorage.getItem('userUUID');

      if (uuid) {
        const response = await logoutUser(uuid);
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('companyName');
        sessionStorage.removeItem('companyUuid');
        sessionStorage.removeItem('userUUID');
        sessionStorage.removeItem('partyUuid');
        sessionStorage.removeItem('shop');
        sessionStorage.removeItem('GuidingStep');
        sessionStorage.removeItem('businessType');
        sessionStorage.removeItem('guidance');
        sessionStorage.removeItem('key');
        sessionStorage.removeItem('planDetail');
        sessionStorage.removeItem('setting');
        sessionStorage.removeItem('dateTimeFormat');
        disconnectSocket();
        navigate('/');
        if (response.ok) {
        } else {
          console.error('Failed to logout:', await response.text());
        }
      }
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  // Dynamically load Razorpay script
  useEffect(() => {
    const loadRazorpayScript = () => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => {
        console.log('Razorpay script loaded successfully');
      };
      script.onerror = () => {
        console.error('Failed to load Razorpay script');
      };
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    };

    loadRazorpayScript();
  }, []);

  useEffect(() => {
    if (subscriptionPlanList && subscriptionPlanList.length) {
      const data = subscriptionPlanList.filter((plan: any) => {
        if (recentPlanType === 'Free') {
          return plan.name === 'Pro' || plan.name === 'Advanced';
        }
        if (recentPlanType === 'Pro') {
          return plan.name === 'Advanced';
        }
        return plan;
      });
      setFilteredPlans(data);
    }
  }, [subscriptionPlanList]);

  const handleRazorpay = async (amount: number, subscription_id: string) => {
    const options = {
      // key: 'rzp_test_gKmnc6lmNNEZ03',
      key: razorPayKey,
      amount: amount * 100,
      currency: subscriptionPlanDetail.currency,
      // currency: 'INR',
      name: 'Sulopa Technology',
      description: 'Test Transaction',
      // image: "https://your-logo-url.com",
      subscription_id: subscription_id,
      handler: async function (response: any) {
        // Callback after payment is successful
        // alert(`Payment ID: ${response.razorpay_payment_id}`);
        // alert(`Order ID: ${response.razorpay_order_id}`);
        // alert(`Signature: ${response.razorpay_signature}`);
        if (response && response.razorpay_payment_id && response.razorpay_signature) {
          const payload = {
            paymentId: response.razorpay_payment_id,
            subscriptionId: subscription_id,
            amount: amount
          };
          const updateSubscriptionResponse = await updateSubscriptionStatus(payload);
          if (updateSubscriptionResponse.ok) {
            const data = await updateSubscriptionResponse.json();
            navigate('/thank-you');
          }
        }
      },
      notes: {
        address: 'Razorpay Corporate Office'
      },
      theme: {
        color: '#3399cc'
      }
    };

    const razorpay = new Razorpay(options);
    razorpay.open();

    razorpay.on('payment.failed', function (response: any) {
      alert(`Error: ${response.error.description}`);
    });
  };

  const handlePayment = async (planType: string, planUuid: string, amount: number) => {
    setDisable(true);
    if (businessType === 'Retail') {
      const planInfo = {
        planUuid: planUuid,
        interval:
          planType === 'Free' ? null : billingType === 'monthly' ? 'EVERY_30_DAYS' : 'ANNUAL'
      };
      const resp = await createRetailerSubscriptionPlan(planInfo, shopId);
      const data = await resp.json();
      if (resp.status === 201 && data.payload) {
        if (planType === 'Free') {
          navigate('/thank-you');
          setDisable(false);
        } else {
          window.location.href = data.payload.confirmationUrl;
        }
      } else {
        setDisable(false);
        ToastNotification({
          message: data?.message,
          type: 'error'
        });
      }
    } else {
      const payload = {
        planUuid: planUuid,
        billingCycle: billingType,
        currency: subscriptionPlanDetail.currency
      };
      const response = updatePlan
        ? await updateSubscription(payload)
        : await createSubscription(payload);
      const data = await response.json();
      if (response.ok) {
        if (data && planType === 'Free') {
          navigate('/thank-you');
          setDisable(false);
        }
        if (data && planType !== 'Free') {
          handleRazorpay(amount, data?.payload?.subscription.razorpaySubscriptionId);
          setDisable(false);
        }
      } else {
        setDisable(false);
        ToastNotification({
          message: data?.message,
          type: 'error'
        });
      }
    }
  };

  return (
    <div className="h-full">
      <div className="flex flex-col md:flex-row gap-3 pt-20 lg:px-20 md:px-20 px-10 pb-12">
        {updatePlan && (
          <div className="sm:w-4 sm:h-4 mr-3">
            <BackButton onClick={() => navigate('/settings/subscription')} />
          </div>
        )}
        <div className="flex flex-col gap-3 w-full">
          <div className="flex items-start">
            <div className="flex flex-col text-white font-semibold text-xl md:text-4xl gap-3">
              <label>We’ve got a plan</label>
              <label>that’s perfect for you</label>
            </div>
          </div>

          <div className="flex">
            <div className="border-2 border-white rounded-lg p-1.5 flex self-start gap-2 items-center mt-4 mb-3">
              <label
                className={`p-2 text-xs md:text-base font-medium  cursor-pointer 
          ${billingType === 'monthly' ? 'bg-white text-[#1E1E1E] rounded-md' : 'text-white'} `}
                onClick={() => {
                  setBillingType('monthly');
                }}
              >
                Monthly billing
              </label>
              <label
                className={`p-2 text-xs md:text-base font-medium  cursor-pointer 
          ${billingType === 'yearly' ? 'bg-white text-[#1E1E1E] rounded-md' : 'text-white'} `}
                onClick={() => setBillingType('yearly')}
              >
                Annual billing
              </label>
            </div>
            <div className="flex items-center space-x-2 text-white">
              <img src={DiscountArrorw} className="ml-2" alt="Discount Arrorw" />
              <span className="text-xs md:text-sm font-medium">Save up to 50%</span>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-[70%] w-full text-white text-lg font-bold">
              <>
                <div className="fixed inset-0 bg-black bg-opacity-50 z-40"></div>

                <div className="fixed inset-0 flex items-center justify-center z-50">
                  <div className="loader border-t-4 border-[#006BEA] rounded-full w-12 h-12 animate-spin"></div>
                </div>
              </>
            </div>
          ) : (
            <div
              className={`grid grid-cols-1  ${
                filteredPlans?.length === 3
                  ? 'lg:grid-cols-3'
                  : filteredPlans?.length === 2
                    ? 'lg:flex max-w-3xl mx-auto'
                    : 'lg:flex max-w-md mx-auto '
              } justify-evenly gap-10 lg:gap-0`}
            >
              {filteredPlans &&
                filteredPlans.map((info: any, index: number) => (
                  <div
                    className="border-2 border-[#006BEA] rounded-3xl p-8 flex flex-col mr-8"
                    key={info.uuid}
                  >
                    <div className="flex justify-between">
                      <label className="font-semibold text-base md:text-lg mb-2">
                        {info.name} Plan
                      </label>
                      {info.name === 'Pro' && (
                        <div>
                          <span className="px-3 py-2 bg-[#DAA520] text-white rounded-md text-xs md:text-sm font-medium">
                            Popular
                          </span>
                        </div>
                      )}
                    </div>
                    {info.name === 'Free' ? (
                      <label className="font-semibold text-xl md:text-4xl mb-2">Free</label>
                    ) : (
                      <label className="font-semibold text-xl md:text-4xl mb-2">
                        {info.planPricing[0] &&
                        info.planPricing[0].currency &&
                        info.planPricing[0].currency === 'INR'
                          ? '₹'
                          : '$'}
                        {billingType === 'monthly' && info.planPricing[0]
                          ? info.planPricing[0].monthlyPrice
                          : info.planPricing[0].yearlyPrice}
                      </label>
                    )}
                    <div
                      className={`rounded-md flex justify-center mt-3 ${disable ? 'bg-[#cccccc] cursor-not-allowed' : 'bg-[#006BEA] cursor-pointer'}`}
                      onClick={() =>
                        disable
                          ? {}
                          : handlePayment(
                              info.name,
                              info.planPricing[0].planUuid,
                              billingType === 'monthly' && info.planPricing[0]
                                ? info.planPricing[0].monthlyPrice
                                : info.planPricing[0].yearlyPrice
                            )
                      }
                    >
                      {info.name === 'Free' ? (
                        <label className="font-medium text-base md:text-lg cursor-pointer py-2">
                          Subscribe
                        </label>
                      ) : (
                        <div className=" py-2 cursor-pointer">
                          <label className="font-medium text-base md:text-lg cursor-pointer">
                            Start Free Trial
                          </label>
                        </div>
                      )}
                    </div>
                    {info.name !== 'Free' && (
                      <div className="text-xs md:text-sm font-normal mt-2">
                        After the 14-days trial ends, your money will be deducted. You can cancel
                        anytime.
                      </div>
                    )}

                    <label className="text-sm md:text-lg font-medium mt-4 mb-5">FEATURES</label>
                    {info.features &&
                      info.features[0].map((item: any, index: number) => (
                        <div className="flex items-start gap-3 mb-3" key={index}>
                          {/* <AiFillCheckCircle /> */}
                          <span className="rounded-3xl py-[2px] px-[2px] md:py-[4px] md:px-[4px] bg-[#006BEA] mt-1">
                            <AiOutlineCheck className="md:text-sm text-xs" />
                          </span>
                          <label className="text-sm md:text-base font-medium">{item}</label>
                        </div>
                      ))}
                  </div>
                ))}
            </div>
          )}

          <div>
            <label
              className="px-6 py-2 mt-2 mr-7 border-[#006BEA] border-2 text-white font-normal text-sm md:text-base rounded-lg cursor-pointer float-end"
              onClick={() => handleLogoutClick()}
            >
              Logout
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
